import { Box, Button, Flex, Icon, Table as ChakraTable, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue } from '@chakra-ui/react';
import { flexRender, Row, Table } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { MdAdd, MdArrowDownward, MdArrowUpward, MdOutlineCheckCircleOutline } from 'react-icons/md';
import TablePagination from 'components/list/TablePagination';

export const LIST_MUTED_COLOR = 'secondaryGray.700';

// Titre d'une liste, avec ses filtres éventuels et son bouton d'ajout
export function ListHeader(props: { title: string; addLabel?: string; onAdd?: () => void; children?: ReactNode }) {
	const { title, addLabel, onAdd, children } = props;
	const textColor = useColorModeValue('secondaryGray.900', 'white');
	return (
		<Flex px='25px' mb='12px' align='center' gap='12px'>
			<Text color={textColor} fontSize='20px' fontWeight='700' lineHeight='100%' me='auto'>
				{title}
			</Text>
			{children}
			{addLabel && (
				<Button
					variant='darkBrand'
					color='white'
					size='sm'
					fontWeight='500'
					borderRadius='70px'
					px='20px'
					flexShrink={0}
					leftIcon={<MdAdd />}
					onClick={onAdd}>
					{addLabel}
				</Button>
			)}
		</Flex>
	);
}

// Oui ou non dans une liste : seul le oui est marqué, un non n'est pas une alerte
export function ListCheck(props: { checked: boolean }) {
	return props.checked ? (
		<Icon as={MdOutlineCheckCircleOutline} aria-label='Oui' color='green.500' w='20px' h='20px' verticalAlign='middle' />
	) : (
		<Text as='span' color={LIST_MUTED_COLOR}>
			—
		</Text>
	);
}

// Ligne d'une liste, atténuée si `muted`. `cellContext` s'ajoute à ce que reçoivent les cellules :
// une valeur lue une fois pour la ligne peut ainsi servir à plusieurs colonnes
export function ListRow<T>(props: { row: Row<T>; muted?: boolean; cellContext?: object }) {
	const { row, muted, cellContext } = props;
	const textColor = useColorModeValue('secondaryGray.900', 'white');
	const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
	const stripeBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.50');
	const hoverBg = useColorModeValue('secondaryGray.400', 'whiteAlpha.100');
	return (
		<Tr color={muted ? LIST_MUTED_COLOR : textColor} _even={{ bg: stripeBg }} _hover={{ bg: hoverBg }}>
			{row.getVisibleCells().map((cell) => (
				<Td
					key={cell.id}
					textAlign={cell.column.columnDef.meta?.align ?? 'left'}
					px='12px'
					py='6px'
					h='44px'
					fontSize='sm'
					overflowWrap='anywhere'
					borderColor={borderColor}
					_first={{ ps: '25px' }}
					_last={{ pe: '25px' }}>
					{flexRender(cell.column.columnDef.cell as any, { ...cell.getContext(), ...cellContext })}
				</Td>
			))}
		</Tr>
	);
}

// Liste compacte, lisible sur un ordinateur portable. Elle défile dans sa carte, qui tient dans l'écran :
// l'en-tête et la pagination restent visibles. Les largeurs viennent du `meta` des colonnes, pour qu'elles
// ne bougent pas pendant la lecture des cellules ; la colonne sans largeur prend le reste
export default function ListTable<T>(props: { table: Table<T>; renderRow: (row: Row<T>) => ReactNode }) {
	const { table, renderRow } = props;
	const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
	const cardBg = useColorModeValue('white', 'navy.800');
	return (
		<>
			<Box
				overflow='auto'
				maxH={{ xl: `calc(100vh - ${table.getPageCount() > 1 ? 262 : 210}px)` }}
				sx={{ overscrollBehaviorY: 'contain' }}>
				<ChakraTable
					variant='simple'
					color='gray.500'
					minW='860px'
					sx={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
					<Thead>
						{table.getHeaderGroups().map((headerGroup) => (
							<Tr key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									const sorted = header.column.getIsSorted();
									return (
										<Th
											key={header.id}
											colSpan={header.colSpan}
											w={header.column.columnDef.meta?.width}
											textAlign={header.column.columnDef.meta?.align ?? 'left'}
											position='sticky'
											top='0'
											zIndex='1'
											bg={cardBg}
											px='12px'
											py='8px'
											color={LIST_MUTED_COLOR}
											fontSize='xs'
											whiteSpace='nowrap'
											borderColor={borderColor}
											cursor={header.column.getCanSort() ? 'pointer' : 'default'}
											aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}
											_first={{ ps: '25px' }}
											_last={{ pe: '25px' }}
											onClick={header.column.getToggleSortingHandler()}>
											{flexRender(header.column.columnDef.header, header.getContext())}
											{sorted && (
												<Icon
													as={sorted === 'asc' ? MdArrowUpward : MdArrowDownward}
													ms='4px'
													verticalAlign='text-bottom'
												/>
											)}
										</Th>
									);
								})}
							</Tr>
						))}
					</Thead>
					<Tbody>{table.getRowModel().rows.map(renderRow)}</Tbody>
				</ChakraTable>
			</Box>
			<Box pt='10px'>
				<TablePagination table={table} />
			</Box>
		</>
	);
}
